import {
  getPromoStoreOrderItems,
  isLikelyPromoStoreTestOrder,
  normalizeReportGroupValue,
} from "../utils/promoStoreOrderItems";
import { resolvePromoStoreItemId } from "../utils/promoStoreSyncIdentity";
import {
  getProductPerformanceOrderStatus,
  isProductPerformanceSaleEligible,
  normalizeProductPerformanceStatus,
} from "./productPerformanceEligibility";

const normalizeLookupValue = (value) => String(value ?? "").trim().toLowerCase();

const getOrderDateValue = (order = {}) => {
  const raw = order?.date_created ?? order?.createdAt ?? order?.created_at
    ?? order?.order_date ?? order?.OrderDate ?? order?.dateCreated;
  const date = raw ? new Date(raw) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const getOrderCustomerName = (order = {}) => {
  const direct = order?.customer_name ?? order?.CustomerName ?? order?.customerName
    ?? order?.customer ?? order?.Customer;
  if (direct && typeof direct === "string") return direct;
  const billing = order?.billing || order?.billingAddress || order?.rawPayload?.billing || {};
  return [
    billing.first_name ?? billing.firstName ?? order?.billing_first_name,
    billing.last_name ?? billing.lastName ?? order?.billing_last_name,
  ].filter(Boolean).join(" ") || billing.email || order?.customerEmail
    || order?.customer_email || order?.CustomerEmail || "Unknown customer";
};

const getOrderId = (order = {}) => String(
  order?.id ?? order?.ID ?? order?.orderId ?? order?.OrderId ?? order?.order_id
    ?? order?.wooOrderId ?? ""
);

const getOrderCurrency = (order = {}, fallback = "USD") => (
  order?.currency ?? order?.Currency ?? order?.currency_code ?? order?.CurrencyCode ?? fallback
);

const getVariationAttribute = (item = {}, attributeName = "") => {
  const target = normalizeLookupValue(attributeName);
  const variationEntries = Array.isArray(item?.raw?.variation) ? item.raw.variation : [];
  const metaEntries = Array.isArray(item?.raw?.meta_data) ? item.raw.meta_data : [];
  const entries = [...variationEntries, ...metaEntries];
  const matchedEntry = entries.find((entry) => {
    const name = normalizeLookupValue(entry?.name ?? entry?.attribute ?? entry?.key).replace(/^attribute_/, "");
    return name === target || name.endsWith(`_${target}`);
  });
  if (matchedEntry) return String(matchedEntry.option ?? matchedEntry.value ?? "").trim();

  const variationParts = String(item.variation || "").split("|");
  const matchedPart = variationParts.find((part) => (
    normalizeLookupValue(part.split(":")[0]).replace(/^attribute_/, "") === target
  ));
  return matchedPart ? matchedPart.split(":").slice(1).join(":").trim() : "";
};

const addWeightedValue = (map, value, quantity) => {
  const normalized = normalizeLookupValue(value);
  if (!normalized) return;
  const current = map.get(normalized) || { label: String(value).trim(), quantity: 0 };
  current.quantity += quantity;
  map.set(normalized, current);
};

const getMostFrequentValue = (map) => Array.from(map.values())
  .sort((a, b) => b.quantity - a.quantity || a.label.localeCompare(b.label))[0]?.label || "N/A";

const addUnique = (map, key, value) => {
  if (!key) return;
  if (map.has(key)) {
    map.set(key, null);
    return;
  }
  map.set(key, value);
};

export const buildCatalogProductLookups = (products = []) => {
  const byItemId = new Map();
  const bySku = new Map();
  const byPricingSku = new Map();
  const byName = new Map();

  products.forEach((product) => {
    const itemId = normalizeLookupValue(product?.id ?? product?.PromoStoreItemId ?? product?.promoStoreItemId);
    addUnique(byItemId, itemId, product);
    addUnique(bySku, normalizeLookupValue(product?.SKU ?? product?.sku), product);
    addUnique(byName, normalizeLookupValue(product?.Name ?? product?.name), product);

    const pricings = Array.isArray(product?.pricings) ? product.pricings : [];
    pricings.forEach((pricing) => {
      addUnique(byPricingSku, normalizeLookupValue(pricing?.SKU ?? pricing?.sku), product);
    });
  });

  return { byItemId, bySku, byPricingSku, byName };
};

const getRawLine = (item = {}) => (
  item.raw?.rawPayload && typeof item.raw.rawPayload === "object"
    ? item.raw.rawPayload
    : item.raw || {}
);

const getDirectPromoStoreItemId = (item = {}) => {
  const rawLine = getRawLine(item);
  const lineMeta = item.raw?.meta && typeof item.raw.meta === "object" ? item.raw.meta : {};
  return item.raw?.PromoStoreItemId ?? item.raw?.promoStoreItemId
    ?? rawLine?.PromoStoreItemId ?? rawLine?.promoStoreItemId
    ?? lineMeta?.PromoStoreItemId ?? lineMeta?.promoStoreItemId ?? "";
};

export const resolveCatalogProduct = ({ item = {}, lookups, promoStoreId = "" }) => {
  const directItemId = getDirectPromoStoreItemId(item);
  const resolvedItemId = resolvePromoStoreItemId({
    directItemId,
    line: item.raw,
    promoStoreId,
  });
  const sku = normalizeLookupValue(item.sku);
  const name = normalizeLookupValue(item.productName);

  return lookups.byItemId.get(normalizeLookupValue(resolvedItemId))
    || lookups.byPricingSku.get(sku)
    || lookups.bySku.get(sku)
    || lookups.byName.get(name)
    || null;
};

const getParentSku = (product = {}) => {
  const parentSku = String(product?.SKU ?? product?.sku ?? "").trim();
  if (parentSku) return parentSku;
  const childSkus = new Set(
    (Array.isArray(product?.pricings) ? product.pricings : [])
      .map((pricing) => String(pricing?.SKU ?? pricing?.sku ?? "").trim())
      .filter(Boolean)
  );
  return childSkus.size === 1 ? Array.from(childSkus)[0] : "";
};

const productMatchesSearch = (product = {}, searchNeedle = "") => {
  if (!searchNeedle) return true;
  const childSkus = (Array.isArray(product?.pricings) ? product.pricings : [])
    .map((pricing) => pricing?.SKU ?? pricing?.sku ?? "");
  return [product?.Name, product?.name, product?.SKU, product?.sku, ...childSkus]
    .some((value) => normalizeReportGroupValue(value).includes(searchNeedle));
};

const filterSalesOrders = (orders = [], filters = {}) => {
  const from = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`) : null;
  const to = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59`) : null;
  const customerNeedle = normalizeReportGroupValue(filters.customer);
  const selectedStatus = filters.orderStatus && filters.orderStatus !== "all"
    ? normalizeProductPerformanceStatus(filters.orderStatus)
    : "";

  return orders.filter((order) => {
    if (isLikelyPromoStoreTestOrder(order)) return false;
    const orderDate = getOrderDateValue(order);
    if (from && (!orderDate || orderDate < from)) return false;
    if (to && (!orderDate || orderDate > to)) return false;
    if (customerNeedle && !normalizeReportGroupValue(getOrderCustomerName(order)).includes(customerNeedle)) return false;
    if (!isProductPerformanceSaleEligible(order)) return false;
    if (selectedStatus && normalizeProductPerformanceStatus(getProductPerformanceOrderStatus(order)) !== selectedStatus) return false;
    return true;
  });
};

const makeBaseRow = (product = {}) => {
  const id = product?.id ?? product?.PromoStoreItemId ?? product?.promoStoreItemId;
  const productName = String(product?.Name ?? product?.name ?? "").trim() || "Unnamed item";
  return {
    key: `catalog:${id}`,
    groupKey: `catalog:${id}`,
    catalogProduct: product,
    promoStoreItemId: id,
    productId: product?.ProductId ?? product?.productId ?? "",
    variationId: "",
    product: productName,
    displayProduct: productName,
    sku: getParentSku(product),
    variation: "N/A",
    orderIds: new Set(),
    lineCount: 0,
    unitsSold: 0,
    revenue: 0,
    sizes: new Map(),
    colors: new Map(),
  };
};

export const buildCatalogProductPerformance = ({
  products = [],
  orders = [],
  filters = {},
  promoStoreId = "",
  currencyCode = "USD",
} = {}) => {
  const searchNeedle = normalizeReportGroupValue(filters.search);
  const reportableProducts = products.filter((product) => {
    const id = product?.id ?? product?.PromoStoreItemId ?? product?.promoStoreItemId;
    return id !== undefined && id !== null && String(id).trim() && productMatchesSearch(product, searchNeedle);
  });
  const rowsById = new Map(reportableProducts.map((product) => {
    const row = makeBaseRow(product);
    return [normalizeLookupValue(row.promoStoreItemId), row];
  }));
  const lookups = buildCatalogProductLookups(products);
  const trend = new Map();
  const unmatched = { lineCount: 0, unitsSold: 0, revenue: 0 };
  let reportCurrency = currencyCode || "USD";

  filterSalesOrders(orders, filters).forEach((order) => {
    const orderId = getOrderId(order);
    const orderDate = getOrderDateValue(order);
    reportCurrency = getOrderCurrency(order, reportCurrency);

    getPromoStoreOrderItems(order).forEach((item) => {
      const quantity = Number(item.quantity) || 0;
      if (quantity <= 0) return;
      const product = resolveCatalogProduct({ item, lookups, promoStoreId });
      if (!product) {
        unmatched.lineCount += 1;
        unmatched.unitsSold += quantity;
        unmatched.revenue += Number(item.lineTotal) || 0;
        return;
      }

      const productId = normalizeLookupValue(product?.id ?? product?.PromoStoreItemId ?? product?.promoStoreItemId);
      const row = rowsById.get(productId);
      if (!row) return;

      row.orderIds.add(orderId || `${row.key}:${row.orderIds.size}`);
      row.lineCount += 1;
      row.unitsSold += quantity;
      row.revenue += Number(item.lineTotal) || 0;
      const rawLine = getRawLine(item);
      const metadataItem = { ...item, raw: rawLine };
      addWeightedValue(row.sizes, getVariationAttribute(metadataItem, "size"), quantity);
      addWeightedValue(
        row.colors,
        getVariationAttribute(metadataItem, "color") || getVariationAttribute(metadataItem, "colour"),
        quantity
      );

      if (orderDate) {
        const key = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, "0")}`;
        const current = trend.get(key) || { label: key, unitsSold: 0, revenue: 0 };
        current.unitsSold += quantity;
        current.revenue += Number(item.lineTotal) || 0;
        trend.set(key, current);
      }
    });
  });

  const rows = Array.from(rowsById.values())
    .map((row) => ({
      ...row,
      orders: row.orderIds.size,
      itemsQuantity: row.lineCount,
      unitPrice: row.unitsSold > 0 ? row.revenue / row.unitsSold : null,
      averageQuantityPerOrder: row.orderIds.size > 0 ? row.unitsSold / row.orderIds.size : 0,
      itemsLeft: null,
      trend: "N/A",
      topSize: getMostFrequentValue(row.sizes),
      topColor: getMostFrequentValue(row.colors),
      orderIds: undefined,
      sizes: undefined,
      colors: undefined,
    }))
    .sort((a, b) => b.unitsSold - a.unitsSold || a.product.localeCompare(b.product));

  const matchedOrderIds = new Set();
  Array.from(rowsById.values()).forEach((row) => row.orderIds.forEach((orderId) => matchedOrderIds.add(orderId)));
  const soldRows = rows.filter((row) => row.unitsSold > 0);

  return {
    rows,
    soldRows,
    currency: reportCurrency,
    trend: Array.from(trend.values()).sort((a, b) => a.label.localeCompare(b.label)),
    unmatched,
    summary: {
      totalProducts: rows.length,
      totalUnitsSold: rows.reduce((sum, row) => sum + row.unitsSold, 0),
      productRevenue: rows.reduce((sum, row) => sum + row.revenue, 0),
      totalOrders: matchedOrderIds.size,
      bestSeller: soldRows[0]?.product || "N/A",
    },
  };
};
